import {
    createAsyncThunk,
    createSelector,
    createSlice,
    PayloadAction,
} from '@reduxjs/toolkit';
import { ObjectId } from '@snackpass/snackpass-types/build/v1/globals';
import api from '../../API';
import { RootState } from '../store';

export enum ScreenType {
    ors = 'ORS',
    dmb = 'DMB',
    image = 'IMAGE',
    video = 'VIDEO',
}

export enum ChannelType {
    CustomerPickupScreen = 'CustomerReadyScreen',
    FullScreenGallery = 'FullScreenGallery',
    Video = 'Video',
    StaticImage = 'StaticImage',
    InfiniteScrollImage = 'InfiniteScrollImage',
    // @deprecated
    Top8Cards = 'Top8Cards',
    // @deprecated
    ColumnScroll = 'ColumnScroll',
    // @deprecated
    InfiniteScrollText = 'InfiniteScrollText',
    // @deprecated
    MenuCategories = 'MenuCategories',
}

export type PinnedCategory = {
    category: string;
    order: number;
};

export type PinnedProduct = {
    productId: string;
    badge: string;
    order: number;
};

export enum ChannelTheme {
    LIGHT = 'Light',
    DARK = 'Dark',
}

export enum Font {
    Inter = 'Inter',
    Literata = 'Literata',
    Satoshi = 'Satoshi',
    Zodiak = 'Zodiak',
    PilcrowRounded = 'PilcrowRounded',
    MonaSans = 'Mona-Sans',
    Unbounded = 'Unbounded',
    Bitter = 'Bitter',
    Vollkorn = 'Vollkorn',
    Antonio = 'Antonio',
    SplineSans = 'Spline-Sans',
    Supreme = 'Supreme',
    Alpino = 'Alpino',
    Bevellier = 'Bevellier',
    PlusJakartaSans = 'Plus-Jakarta',
    Teko = 'Teko',
}
export enum Voice {
    LIANNA = 'lhaxjiPTIcNO0GNEl4uM',
    DAVID = 'zKwoXTAlqUwPULnc39aL',
    TYRELL = 'WaCUI7bZkGODe2MPSAMu',
    DAKOTA = 'P7x743VjyZEOihNNygQ9',
    VALERIA = '9oPKasc15pfAbMr7N6Gs',
    ANTONIO = 'UfCRQShYrGoa6BYs8jnn',
    AMELIA = 'w75jSo40IU4IPsNaIM8W',
    COCO = 'ngiiW8FFLIdMew1cqwSB',
    VIKRANT = 'y6Ao4Y93UrnTbmzdVlFc',
    PRIYA = '2zRM7PkgwBPiau2jvVXc',
}

const DEFAULT_VOICE = Voice.LIANNA;

export type VoiceDetails = {
    name: string;
    origin: string;
};

export const voiceDetails: Record<Voice, VoiceDetails> = {
    [Voice.LIANNA]: { name: 'Lianna', origin: 'American' },
    [Voice.DAVID]: { name: 'David', origin: 'American' },
    [Voice.TYRELL]: { name: 'Tyrell', origin: 'American' },
    [Voice.DAKOTA]: { name: 'Dakota', origin: 'American, Southern' },
    [Voice.VALERIA]: { name: 'Valeria', origin: 'Argentine' },
    [Voice.ANTONIO]: { name: 'Antonio', origin: 'Latin American' },
    [Voice.AMELIA]: { name: 'Amelia', origin: 'British' },
    [Voice.COCO]: { name: 'Coco', origin: 'Chinese' },
    [Voice.VIKRANT]: { name: 'Vikrant', origin: 'Indian' },
    [Voice.PRIYA]: { name: 'Yamsin', origin: 'Indian' },
};

export enum Layout {
    CLASSIC = 'CLASSIC',
}

export enum Fulfillment {
    PICKUP = 'PICKUP',
    DINE_IN = 'DINE_IN',
    DELIVERY = 'DELIVERY',
}

export type CustomerPickupScreenOptions = {
    onlyShowFirstInitial: boolean;
    font: Font;
    fontSize: number;
    layout: Layout;
    audioOn: boolean;
    voice: Voice;
    fulfillment: Fulfillment[];
};

export type FullScreenGalleryOptions = {
    pinnedProducts: PinnedProduct[];
    hideSoldOut: boolean;
    pageInterval: number;
};

export type MenuCategoriesOptions = {
    pinnedCategories: PinnedCategory[];
    hideSoldOut: boolean;
    hideDescription: boolean;
    pageInterval: number;
    font: Font;
    fontSize: number;
    theme: ChannelTheme;
};

export type ColumnScrollOptions = {
    pinnedCategories: PinnedCategory[];
    hideSoldOut: boolean;
    hideDescription: boolean;
    pageInterval: number;
    font: Font;
    fontSize: number;
    theme: ChannelTheme;
};

export type InfiniteScrollOptions = {
    pinnedCategories: PinnedCategory[];
    pinnedProducts: PinnedProduct[];
    hideSoldOut: boolean;
    enableScroll: boolean;
    pageInterval: number;
    font: Font;
    fontSize: number;
    theme: ChannelTheme;
    headerColor?: string;
    menuBoardBackgroundColor?: string;
    productBackgroundColor?: string;
    imageWidth: number;
    imageHeight: number;
};

export type StaticImageOptions = {
    imageURL?: string | null;
};

export interface IChannelTypeOptions {
    [ChannelType.CustomerPickupScreen]: CustomerPickupScreenOptions;
    [ChannelType.Video]: {};
    [ChannelType.Top8Cards]: {};
    [ChannelType.StaticImage]: StaticImageOptions;
    [ChannelType.ColumnScroll]: ColumnScrollOptions;
    [ChannelType.FullScreenGallery]: FullScreenGalleryOptions;
    [ChannelType.MenuCategories]: MenuCategoriesOptions;
    [ChannelType.InfiniteScrollImage]: InfiniteScrollOptions;
    [ChannelType.InfiniteScrollText]: InfiniteScrollOptions;
}

export type ChannelOption<TChannel extends keyof IChannelTypeOptions = any> = {
    channel: TChannel;
    options: IChannelTypeOptions[TChannel];
};

const DEFAULT_CHANNEL_OPTIONS: CustomerPickupScreenOptions = {
    fontSize: 22,
    font: Font.Satoshi,
    audioOn: true,
    voice: DEFAULT_VOICE,
    layout: Layout.CLASSIC,
    onlyShowFirstInitial: false,
    fulfillment: [Fulfillment.PICKUP, Fulfillment.DINE_IN, Fulfillment.DELIVERY],
};

export enum ScreenStatus {
    LOADING = 'LOADING',
    REGISTERING = 'REGISTERING',
    REGISTERED = 'REGISTERED',
    FAILED_TO_SIGN_IN = 'FAILED_TO_SIGN_IN',
}

export interface IScreen {
    id: ObjectId;
    store: ObjectId;
    serial: string;
    screenName?: string;
    lastPing: Date;
    screenType: ScreenType;
    channel: ChannelType;
    channelOptions?: Array<ChannelOption>;
}

interface ScreenState {
    activeScreen: IScreen | null;
    status: ScreenStatus;
}

const initialState: ScreenState = {
    activeScreen: null,
    status: ScreenStatus.LOADING,
};

export const getScreenByDeviceId = createAsyncThunk(
    'screen/getScreenByDeviceId',
    async ({ storeId, deviceId }: { storeId: string; deviceId: string }) => {
        if (storeId !== '' && deviceId !== '') {
            const response = await api.screens.getScreenByDeviceId(
                storeId,
                deviceId
            );
            return response.data.screen;
        }
        return null;
    }
);

export const updateScreen = createAsyncThunk(
    'screen/updateScreen',
    async ({
        screenId,
        updates,
    }: {
        screenId: string;
        updates: Partial<IScreen>;
    }) => {
        const response = await api.screens.update(screenId, updates);
        return response.data.screen;
    }
);

const screenSlice = createSlice({
    name: 'screen',
    initialState,
    reducers: {
        setScreen(state: ScreenState, action: PayloadAction<IScreen>) {
            if (
                state.activeScreen?.id === action.payload.id ||
                state.activeScreen === null
            ) {
                state.activeScreen = action.payload;
            }
        },
        setScreenStatus(
            state: ScreenState,
            action: PayloadAction<ScreenStatus>
        ) {
            state.status = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(getScreenByDeviceId.pending, (state: ScreenState) => {
                state.activeScreen = null;
            })
            .addCase(
                getScreenByDeviceId.fulfilled,
                (state: ScreenState, action: PayloadAction<IScreen | null>) => {
                    state.activeScreen = action.payload;
                }
            )
            .addCase(getScreenByDeviceId.rejected, (state: ScreenState) => {
                state.activeScreen = null;
            })
            .addCase(updateScreen.pending, (state: ScreenState) => {
                state.activeScreen = null;
            })
            .addCase(
                updateScreen.fulfilled,
                (state: ScreenState, action: PayloadAction<IScreen | null>) => {
                    state.activeScreen = action.payload;
                }
            )
            .addCase(updateScreen.rejected, (state: ScreenState) => {
                state.activeScreen = null;
            });
    },
});

export const getScreenState = (state: RootState) => state.screen;

export const getActiveScreen = createSelector(
    [getScreenState],
    state => state.activeScreen
);

export const getScreenStatus = createSelector(
    [getScreenState],
    state => state.status
);

export const getActiveScreenChannelOptions = createSelector(
    [getActiveScreen],
    activeScreen =>
        activeScreen?.channelOptions?.find(
            c => c.channel === activeScreen.channel
        )
);

export const getFontSize = createSelector(
    [getActiveScreenChannelOptions],
    channelOptions =>
        channelOptions?.options?.fontSize ?? DEFAULT_CHANNEL_OPTIONS.fontSize
);

export const getAudioStatus = createSelector(
    [getActiveScreenChannelOptions],
    channelOptions =>
        channelOptions?.options?.audioOn ?? DEFAULT_CHANNEL_OPTIONS.audioOn
);

export const getVoice = createSelector(
    [getActiveScreenChannelOptions],
    channelOptions => {
        // there may be some legacy voices that are no longer in the enum
        // but their eleven labs id still exists
        // it just will not show up in options
        const voice = channelOptions?.options?.voice;
        return voice || DEFAULT_CHANNEL_OPTIONS.voice;
    }
);
export const getLayout = createSelector(
    [getActiveScreenChannelOptions],
    channelOptions =>
        channelOptions?.options?.layout ?? DEFAULT_CHANNEL_OPTIONS.layout
);
export const getFont = createSelector(
    [getActiveScreenChannelOptions],
    channelOptions =>
        channelOptions?.options?.font ?? DEFAULT_CHANNEL_OPTIONS.font
);
export const getOnlyShowFirstInitial = createSelector(
    [getActiveScreenChannelOptions],
    channelOptions =>
        channelOptions?.options?.onlyShowFirstInitial ??
        DEFAULT_CHANNEL_OPTIONS.onlyShowFirstInitial
);

export const getFulfillment = createSelector(
    [getActiveScreenChannelOptions],
    channelOptions =>
        channelOptions?.options?.fulfillment ?? DEFAULT_CHANNEL_OPTIONS.fulfillment
);

export const { setScreen, setScreenStatus } = screenSlice.actions;
export const screenReducer = screenSlice.reducer;
